import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { Moon, Sun, Languages } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface JourneyHeaderProps {
    currentSection: number;
    totalSections: number;
    section: {
        title: string;
        subtitle?: string;
        icon: string;
    };
    onThemeToggle: () => void;
    onLanguageToggle: () => void;
    language: string;
}

const JourneyHeader: React.FC<JourneyHeaderProps> = ({
    currentSection,
    totalSections,
    section,
    onThemeToggle,
    onLanguageToggle,
    language,
}) => {
    const { isDark } = useTheme();

    return (
        <header className="fixed top-0 left-0 right-0 max-w-6xl mx-auto z-50 bg-white/90 dark:bg-romantic-dark/90 backdrop-blur-xl border-b border-gray-100 dark:border-gray-800 px-4 py-3 md:px-6 rounded-b-[1.5rem] shadow-sm">
            <div className="flex justify-between items-center relative h-12">
                {/* Left: App Title */}
                <motion.h1
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="text-lg md:text-xl font-playfair font-bold text-primary-red z-10"
                >
                    Us50❤️
                </motion.h1>

                {/* Center: Section Title and Progress */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={section.title}
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -5 }}
                            className="flex flex-col items-center"
                        >
                            <div className="flex items-center space-x-2">
                                <span className="text-lg md:text-xl">{section.icon}</span>
                                <h2 className="text-sm md:text-base font-playfair font-bold text-primary-red line-clamp-1 max-w-[120px] md:max-w-[300px]">
                                    {section.title}
                                </h2>
                            </div>
                            <div className="bg-primary-red/5 px-2 py-0.5 rounded-full mt-0.5">
                                <span className="text-[10px] text-primary-red font-bold tracking-tighter">
                                    {currentSection + 1} / {totalSections}
                                </span>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* Right: Controls */}
                <div className="flex items-center space-x-2 md:space-x-3 z-10">
                    <button
                        onClick={onThemeToggle}
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400 hover:bg-gray-100 transition-colors"
                    >
                        {isDark ? <Sun size={16} /> : <Moon size={16} />}
                    </button>

                    <button
                        onClick={onLanguageToggle}
                        className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400 hover:bg-gray-100 transition-colors"
                    >
                        <Languages size={14} />
                        <span className="font-bold text-[10px]">{language === 'en' ? 'தமிழ்' : 'ENG'}</span>
                    </button>
                </div>
            </div>

            {/* Progress Bar (at the very bottom of the compact header) */}
            <div className="absolute bottom-0 left-0 h-0.5 bg-gray-100 dark:bg-gray-800 w-full rounded-full overflow-hidden">
                <motion.div
                    className="h-full bg-primary-red/40"
                    initial={{ width: 0 }}
                    animate={{ width: `${((currentSection + 1) / totalSections) * 100}%` }}
                    transition={{ duration: 0.5 }}
                />
            </div>
        </header>
    );
};

export default JourneyHeader;
